/**
 * A stand-in for Tabulator's `Cell` object, so the ~46 existing model files can
 * keep their `formatter(cell)` / `cellClick(e, cell)` / `contextMenu` /
 * `clickMenu` callbacks unchanged while rendering through v-data-table.
 *
 * The hard part is `cell.getElement()`. Formatters don't just read from it —
 * they mutate it DURING formatting, before any DOM exists:
 *
 *   cell.getElement().classList.add("tbl-cell--warning")   // hrdc models
 *   cell.getElement().style.backgroundColor = "#fff3cd"    // submissionDate
 *
 * So `getElement()` returns a collector: a stub with a working `classList` and
 * a plain-object `style`. After running the formatter, the renderer harvests
 * what was collected and binds it onto the real <td> via :class / :style.
 * Same visual result, zero model edits.
 */

import { getFieldValue } from "./fieldValue";

function createCollector() {
  const classes = [];
  const style = {};
  const attrs = {};

  const classList = {
    add(...names) {
      names.forEach((n) => {
        if (n && classes.indexOf(n) === -1) classes.push(n);
      });
    },
    remove(...names) {
      names.forEach((n) => {
        const i = classes.indexOf(n);
        if (i > -1) classes.splice(i, 1);
      });
    },
    toggle(name, force) {
      const has = classes.indexOf(name) > -1;
      const on = typeof force === "boolean" ? force : !has;
      if (on) classList.add(name);
      else classList.remove(name);
      return on;
    },
    contains: (name) => classes.indexOf(name) > -1,
  };

  return {
    classes,
    style,
    attrs,
    // Enough of an Element surface that a formatter poking at it won't throw.
    element: {
      classList,
      style,
      // Recorded rather than discarded: tintCell() stamps `data-cell-tint`
      // here, and the dark theme keys off it to replace the pale inline
      // background the formatter also set.
      setAttribute(name, value) {
        attrs[name] = String(value);
      },
      removeAttribute(name) {
        delete attrs[name];
      },
      getAttribute: (name) =>
        Object.prototype.hasOwnProperty.call(attrs, name) ? attrs[name] : null,
      querySelector: () => null,
      querySelectorAll: () => [],
      appendChild: () => null,
      insertAdjacentHTML() {},
      insertAdjacentElement() {},
      children: [],
      dataset: {},
    },
  };
}

/**
 * @param {object} opts
 * @param {object} opts.row        the row data object
 * @param {object} opts.column     the ADAPTED header (carries `_def`, the original Tabulator def)
 * @param {Array}  opts.tableData  all rows on the current page
 * @returns {{cell: object, collected: {classes: string[], style: object}}}
 */
export function createCellFacade({ row, column, tableData }) {
  const collector = createCollector();
  const rowCollector = createCollector();
  const field = column.field || column.value;
  const definition = column._def || {};

  const rowFacade = {
    getData: () => row,
    getElement: () => rowCollector.element,
    getCells: () => [],
    // No-op: v-data-table re-renders reactively, so there is nothing to reformat.
    reformat: () => {},
    getIndex: () => row.rownum,
    update: (patch) => Object.assign(row, patch),
  };

  const columnFacade = {
    getDefinition: () => definition,
    getField: () => field,
    getElement: () => createCollector().element,
  };

  const cell = {
    getValue: () => getFieldValue(row, field),
    getOldValue: () => getFieldValue(row, field),
    getInitialValue: () => getFieldValue(row, field),
    getData: () => row,
    getField: () => field,
    getElement: () => collector.element,
    getRow: () => rowFacade,
    getColumn: () => columnFacade,
    getTable: () => ({ getData: () => tableData || [] }),
    // Editing is out of scope for v1; these keep legacy callbacks from throwing.
    setValue: (v) => {
      if (field) row[field] = v;
    },
    restoreOldValue: () => {},
    validate: () => true,
    checkHeight: () => {},
  };

  return {
    cell,
    collected: collector,
    rowCollected: rowCollector,
  };
}
